import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/seo/SEO";
import { ArrowRight, Car, Shield, Zap } from "lucide-react";
import { openOpenSolar } from "@/lib/widgets";
import { track } from "@/lib/analytics";
const faqs = [{
  q: "Is mijn meterkast geschikt voor een laadpaal?",
  a: "Wij controleren uw meterkast en adviseren over een eventuele uitbreiding of aanpassing. Bij 3-fase laden is vaak een 3-fase aansluiting nodig."
}, {
  q: "Wat is load balancing en heb ik het nodig?",
  a: "Load balancing zorgt dat de laadpaal automatisch het laadvermogen aanpast zodat uw hoofdzekering niet overbelast raakt. Aan te raden bij hogere vermogens of veel gelijktijdig verbruik."
}, {
  q: "Kan ik laden op zonnestroom?",
  a: "Ja, met solar charging geeft u prioriteit aan zelfopgewekte energie. Uw auto laadt dan automatisch meer wanneer uw zonnepanelen produceren."
}, {
  q: "Wat is het verschil tussen 1‑fase en 3‑fase laden?",
  a: "1‑fase laden is tot ca. 7,4 kW, 3‑fase tot 11–22 kW (auto-afhankelijk). 3‑fase is sneller en toekomstbestendiger."
}];
const jsonLd = [{
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Installatie laadpalen thuis en zakelijk",
  "provider": {
    "@type": "Organization",
    "name": "SOL-VI"
  },
  "areaServed": "Nederland",
  "serviceType": "EV-laadpalen",
  "description": "EV-laadpalen met load balancing en PV-sturing. 1- of 3-fase, veilig geïnstalleerd."
}, {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(f => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.a
    }
  }))
}];
export default function Laadpalen() {
  return <div className="min-h-screen bg-background">
      <Header />
      <main>
        <SEO title="Laadpalen thuis en zakelijk | SOL-VI" description="EV-laadpalen met load balancing en PV-sturing. 1- of 3-fase, veilig geïnstalleerd. Gratis advies & snelle plaatsing." canonical="https://sol-vi.nl/laadpalen" type="article" jsonLd={jsonLd} />

        {/* Hero with Video Banner */}
        <section className="relative pt-20 pb-12 overflow-hidden">
          <div className="absolute inset-0 bg-black">
            <video autoPlay muted loop playsInline className="w-full h-full object-cover opacity-40">
              <source src="https://pim.loxone.com/02%20Category%20Data/Energy/Wallbox_webshop_category.webm" type="video/webm" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-background/70"></div>
          </div>
          <div className="relative container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Laadpalen: <span className="text-primary">slim en veilig laden</span>
              </h1>
              <p className="text-lg text-gray-100 mb-8 max-w-3xl mx-auto">
                Thuis of op de zaak: wij plaatsen gecertificeerde laadoplossingen met load balancing, PV-sturing en 1‑/3‑fase opties. Klaar voor nu én de toekomst.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Button variant="cta" size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-glow" onClick={() => {
                track("laadpalen_cta_offerte");
                openOpenSolar();
              }} aria-label="Vraag een gratis offerte aan">
                  Gratis Offerte
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button variant="outline" size="lg" onClick={() => {
                track("laadpalen_cta_calendly");
                (window as any).Calendly?.initPopupWidget?.({
                  url: "https://calendly.com/sol-vi/new-meeting?primary_color=ff7800"
                });
              }} className="border-white/30 hover:bg-white/10 text-slate-950">
                  Plan adviesgesprek
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Why SOL-VI */}
        <section className="py-16">
          <div className="container mx-auto px-4 grid md:grid-cols-3 gap-8">
            <div className="p-6 rounded-lg border bg-card">
              <Shield className="h-6 w-6 text-primary mb-3" />
              <h3 className="text-xl font-semibold mb-2">Veilig en gecertificeerd</h3>
              <p className="text-muted-foreground">Vakkundige installatie, correcte beveiliging en nette afwerking. Wij controleren en upgraden de meterkast waar nodig.</p>
            </div>
            <div className="p-6 rounded-lg border bg-card">
              <Zap className="h-6 w-6 text-primary mb-3" />
              <h3 className="text-xl font-semibold mb-2">Slim laden</h3>
              <p className="text-muted-foreground">Load balancing, dynamic load management en solar charging om maximaal te laden op zelfopgewekte stroom.</p>
            </div>
            <div className="p-6 rounded-lg border bg-card">
              <Car className="h-6 w-6 text-primary mb-3" />
              <h3 className="text-xl font-semibold mb-2">1‑ en 3‑fase opties</h3>
              <p className="text-muted-foreground">Van 7,4 kW tot 11/22 kW, afhankelijk van uw aansluiting en voertuig. Toekomstbestendige oplossing.</p>
            </div>
          </div>
        </section>

        {/* Which solution */}
        <section className="py-8">
          <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
              <h2 className="text-2xl md:text-3xl font-bold">Welke laadoplossing past bij u?</h2>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                <li>Thuisladen: wandlader met vaste kabel of socket, kaart/app-bediening</li>
                <li>Zakelijk laden: meerdere laadpunten, load management en gebruikersbeheer</li>
                <li>PV-integratie: slim laden op zonnestroom en koppeling met thuisbatterij</li>
                <li>Tariefsturing: optioneel laden op daluren of dynamische tarieven</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">Installatieproces</h3>
              <ol className="list-decimal pl-5 space-y-2 text-muted-foreground">
                <li>Gratis advies en schouwing</li>
                <li>Offerte met duidelijk plan en materialen</li>
                <li>Professionele installatie</li>
                <li>Oplevering, keuring en uitleg</li>
              </ol>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Veelgestelde vragen</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {faqs.map((f, i) => <details key={i} className="p-4 rounded-lg border bg-card">
                  <summary className="font-medium cursor-pointer">{f.q}</summary>
                  <p className="mt-2 text-muted-foreground">{f.a}</p>
                </details>)}
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Klaar om te starten?</h2>
            <p className="text-muted-foreground mb-6">Vraag een vrijblijvende offerte aan of plan direct een adviesgesprek.</p>
            <div className="flex justify-center gap-3">
              <Button variant="cta" onClick={() => {
              track("laadpalen_bottom_offerte");
              openOpenSolar();
            }}>
                Offerte aanvragen
              </Button>
              <Button variant="outline" onClick={() => {
              track("laadpalen_bottom_calendly");
              (window as any).Calendly?.initPopupWidget?.({
                url: "https://calendly.com/sol-vi/new-meeting?primary_color=ff7800"
              });
            }}>
                Adviesgesprek
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>;
}